import React, { useContext, useEffect, memo } from 'react';
import {
	Route,
	Switch,
	Redirect,
	useHistory,
	useLocation,
} from 'react-router-dom';
import Home from '../modules/client-home';
import MyServices from '../modules/my-services';
import Nav from '../navComponents/Nav';

import LandingPage from '../marketingComponents/LandingPage';

import AdminSignup from '../admin/adminAuth/AdminSignup';
import AdminLogin from '../admin/adminAuth/AdminLogin';
import Verify from '../authComponents/Verify';
import { useSelector } from 'react-redux';
import ForgotPassword from '../authComponents/ForgotPassword';
import ResetPassword from '../authComponents/ResetPassword';
import Settings from '../appComponents/settings/Settings.js';
import Dashboard from '../admin/dashboard/Dashboard';
import ClientList from '../admin/client/shared/ClientList.js';
import ClientRouter from '../admin/client/ClientRouter.js';
import ServiceDetail from '../appComponents/serviceDetail/ServiceDetail';
import ClientForms from '../modules/client-forms';
import { ClientSpendingTracker } from '../modules/spending-tracker';
import ProtectedRoute from './ProtectedRoute';
import OnboardingProtectedRoute from './OnboardingProtectedRoute';
import AdminRoute from './AdminRoute';
import CreateUser from '../admin/createUser/CreateUser';

// new marketing pages
import LandingPage2 from '../marketingComponents2/pages/landingPageNew/LandingPage';
import ServicePage from '../marketingComponents2/pages/ServicePage/ServicePage.js';
import AboutPage from '../marketingComponents2/pages/about/AboutPage.js';
import SignInSection from '../marketingComponents2/section/SignInSection.js';
import ForgetPasswordSection from '../marketingComponents2/section/ForgetPasswordSection.js';
import SignUpSection from '../marketingComponents2/section/SignUpSection.js';
import BlogPage from '../marketingComponents2/pages/blog/BlogPage.js';
import BlogDetailPage from '../marketingComponents2/pages/blog/BlogDetailPage.js';
import ServicePageNew from '../marketingComponents2/pages/ServicePageNew/ServicePageNew.js';
import TermsOfService from '../pages/TermsOfService';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import LandingFooter from '../marketingComponents2/landingFooter/LandingFooter';
import Header from '../marketingComponents2/header/Header';

// Import the Form Builder component
import FormBuilder from '../admin/formBuilder';

// Import Submissions components
import SubmissionsList from '../admin/submissions/SubmissionsList';
import SubmissionDetail from '../admin/submissions/SubmissionDetail';
import CreateSubmission from '../admin/submissions/CreateSubmission';

// Import Notifications components
import NotificationsPage from '../admin/notifications/NotificationsPage';

// Import Email Test component
import EmailTest from '../admin/test/EmailTest';

// Import Admin Settings components
import NotificationSettings from '../admin/settings/NotificationSettings';

// Import SOI Lead Magnet page
import SOILeadMagnet from '../marketingComponents2/pages/SOILeadMagnet/SOILeadMagnet';

// Import Profile component
import ProfileEnhanced from '../appComponents/profile/ProfileEnhanced';

// Import Onboarding component
import OnboardingWizard from '../appComponents/onboarding/OnboardingWizard';

// Import Spending Tracker Admin components
import {
	AdminSpendingTracker as SpendingTrackerDashboard,
	ClientExpenseTable,
} from '../modules/spending-tracker';

// Import Lookup Keys Manager
import LookupKeysManager from '../admin/lookupKeys/LookupKeysManager';

const ScrollToTop = () => {
	const { pathname, hash } = useLocation();

	useEffect(() => {
		// Only scroll to top if there's no hash fragment and pathname actually changed
		if (!hash) {
			// Use requestAnimationFrame to avoid conflicts with anchor scrolling
			requestAnimationFrame(() => {
				window.scrollTo({
					top: 0,
					left: 0,
					behavior: 'instant',
				});
			});
		}
	}, [pathname, hash]);

	return null;
};

const PageLayout = ({ children }) => {
	return (
		<>
			<Header />
			{children}
			<LandingFooter />
		</>
	);
};

/** Components for displaying routes. Here authetication is checked.  */
function Routes() {
	const currentUser = useSelector((st) => st.user.currentUser);
	const currentAdmin = useSelector((st) => st.admin.currentAdmin);
	const isImpersonating = useSelector((st) => Boolean(st.admin?.isImpersonating));
	const isImpersonatingUser = isImpersonating && Boolean(currentUser && currentUser.username);

	/** !Note: When using the component prop for the Route component, a new instance of this combined component is created, causing the child components (Nav and Verify) to unmount and remount, effectively resetting their state. So for ex: if you intend to call a useEffect once in the component, it will be called repeatedly every time this Route component re-renders. Instead, use render prop for Route component  */
	const withNav = (Component) => (props) => {
		return (
			<>
				<Nav />
				<Component {...props} />
			</>
		);
	};

	return (
		<div data-testid='routes'>
			<ScrollToTop></ScrollToTop>
			<Switch>
				<Route
					exact
					path='/'
					component={
						currentUser && currentUser.username ? RedirectHome : LandingPage2
					}
				/>
				<Route
					exact
					path='/login'
					// Reachable while signed in (#168): SignInSection shows the
					// account-aware panel + add-account form. Only a LIVE admin
					// impersonation view is kept out — the condition must agree with
					// /home's gate (currentUser.username), or an impersonation with an
					// empty user projection bounces /home ↔ /login forever.
					component={isImpersonatingUser ? RedirectHome : SignInSection}
				/>
				<Route
					exact
					path='/signup'
					// Reachable while signed in (#168): a signup here ADDS an account.
					component={isImpersonatingUser ? RedirectHome : SignUpSection}
				/>
				<Route
					exact
					path='/forgot-password'
					component={ForgetPasswordSection}
				/>
				<Route
					exact
					path='/services'
					component={
						currentUser && currentUser.username ? RedirectHome : ServicePageNew
					}
				/>
				<Route
					exact
					path='/about-us'
					component={
						currentUser && currentUser.username ? RedirectHome : AboutPage
					}
				/>
				<Route
					exact
					path='/blog'
					component={
						currentUser && currentUser.username ? RedirectHome : BlogPage
					}
				/>
				<Route
					exact
					path='/blog/:id'
					component={
						currentUser && currentUser.username ? RedirectHome : BlogDetailPage
					}
				/>

				<Route exact path='/verify/:username/:code' render={withNav(Verify)} />
				{/* <Route exact path="/verify/:username/:code">
					<Nav />
					<Verify />
				</Route> */}
				<Route
					exact
					path='/reset-password/:username/:code'
					component={ResetPassword}
				/>

				{/* This is only a partially protected route. The component is shown when user isn't verified but mostly hidden */}
				<Route
					exact
					path='/home'
					render={
						currentUser && currentUser.username ? withNav(Home) : RedirectLogin
					}
				/>
				<ProtectedRoute exact path='/my-services' component={MyServices} />
				<ProtectedRoute exact path='/servicedetail' component={ServiceDetail} />

				{/* Forms routes - requires login but no subscription */}
				<Route
					exact
					path='/forms'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>
				<Route
					exact
					path='/forms/:formId'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>
				<Route
					exact
					path='/forms/category/:categoryName'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>
				<Route
					exact
					path='/forms/submissions'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>
				<Route
					exact
					path='/forms/submissions/:submissionId'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>
				<Route
					exact
					path='/forms/submissions/:submissionId/edit'
					render={
						currentUser && currentUser.username
							? withNav(ClientForms)
							: RedirectLogin
					}
				/>

				<ProtectedRoute exact path='/settings' component={Settings} />
				
				{/* Onboarding route - for users who haven't completed their profile */}
				<Route
					exact
					path='/onboarding'
					render={
						currentUser && currentUser.username
							? withNav(OnboardingWizard)
							: RedirectLogin
					}
				/>
				
				<OnboardingProtectedRoute exact path='/profile' component={ProfileEnhanced} />
				<ProtectedRoute
					exact
					path='/spending-tracker'
					component={ClientSpendingTracker}
				/>

				<Route
					exact
					path='/admin/signup'
					component={
						currentAdmin && currentAdmin.username
							? RedirectAdminDashboard
							: AdminSignup
					}
				/>
				<Route
					exact
					path='/admin/login'
					component={
						currentAdmin && currentAdmin.username
							? RedirectAdminDashboard
							: AdminLogin
					}
				/>
				<AdminRoute exact path='/admin/dashboard' component={Dashboard} />
				<AdminRoute exact path='/admin/clients' component={ClientList} />
				<AdminRoute exact path='/admin/create-user' component={CreateUser} />
				<AdminRoute
					exact
					path='/admin/clients/client-details/:username'
					component={ClientRouter}
				/>

				{/* Form Builder Routes */}
				<AdminRoute exact path='/admin/form-builder' component={FormBuilder} />
				<AdminRoute
					exact
					path='/admin/form-builder/create'
					component={FormBuilder}
				/>
				<AdminRoute
					exact
					path='/admin/form-builder/edit/:id'
					component={FormBuilder}
				/>
				<AdminRoute
					exact
					path='/admin/form-builder/view/:id'
					component={FormBuilder}
				/>

				{/* Submissions Routes */}
				<AdminRoute
					exact
					path='/admin/submissions'
					component={SubmissionsList}
				/>
				<AdminRoute
					exact
					path='/admin/submissions/create'
					component={CreateSubmission}
				/>
				<AdminRoute
					exact
					path='/admin/submissions/:id'
					component={SubmissionDetail}
				/>

				{/* Spending Tracker Routes */}
				<AdminRoute
					exact
					path='/admin/spending-tracker'
					component={SpendingTrackerDashboard}
				/>
				<AdminRoute
					exact
					path='/admin/spending-tracker/client/:clientId'
					component={ClientExpenseTable}
				/>

				{/* Notifications Routes */}
				<AdminRoute
					exact
					path='/admin/notifications'
					component={NotificationsPage}
				/>

				{/* Email Test Route */}
				<AdminRoute exact path='/admin/test/email' component={EmailTest} />

				{/* Admin Settings Routes */}
				<AdminRoute
					exact
					path='/admin/settings/notifications'
					component={NotificationSettings}
				/>

				{/* Lookup Keys Routes */}
				<AdminRoute
					exact
					path='/admin/lookup-keys'
					component={LookupKeysManager}
				/>

				{/* Public routes for legal pages */}
				<Route
					exact
					path='/terms'
					render={() => (
						<PageLayout>
							<TermsOfService />
						</PageLayout>
					)}
				/>
				<Route
					exact
					path='/privacy'
					render={() => (
						<PageLayout>
							<PrivacyPolicy />
						</PageLayout>
					)}
				/>

				{/* SOI Lead Magnet Landing Page - No navigation */}
				<Route exact path='/soi-5-day-plan' component={SOILeadMagnet} />

				{/* captures all admin routes will go to dashboard if signed in, will go to login if not */}
				<Route
					path='/admin'
					component={
						currentAdmin && currentAdmin.username
							? RedirectAdminDashboard
							: RedirectAdminLogin
					}
				/>

				{/* captures all non-admin routes will go to dashboard if signed in, will go to login if not */}
				<Route
					path='/'
					component={
						currentUser && currentUser.username ? RedirectHome : RedirectLogin
					}
				/>

				<Redirect to='/' />
			</Switch>
		</div>
	);
}

const RedirectHome = () => <Redirect to='/home' />;
const RedirectLogin = () => <Redirect to='/login' />;
const RedirectAdminDashboard = () => <Redirect to='/admin/dashboard' />;
const RedirectAdminLogin = () => <Redirect to='/admin/login' />;

export default Routes;
